import { createApi } from '@reduxjs/toolkit/query/react';

import { getData, postData } from '../../services/API';
import {
  questionBankUrl,
  templateByIdUrl,
  templateConfigUrl,
  templateDocUrl,
  templateMasterUrl,
  templateTreeUrl,
  fullSurveyUrl,
  templatePublishedByLocationAndTypeUrl
} from '../../services/API/endpoints';
import { processError, processResponse } from './utils/transformations';

import { compareObjByStringKey } from '../../utils/helpers';

import {
  ICondensedMasterTemplate,
  ICondensedQuestion,
  IFullTemplate,
  Question
} from '../../config/interfaces';

interface IGetMasterTemplatesPayload {
  archived: boolean;
  condensed: boolean;
}

interface IPostMasterTemplatePayload {
  archived: boolean;
  isPublished: boolean;
  questions: ICondensedQuestion[];
  templateConfigId?: string;
  templateId?: string;
  lastUpdated: string;
  lastUpdatedBy: string;
  lastUpdatedByName: string;
  lastUpdatedDisplay: string;
  lastUpdatedUTC: string;
  conditions?: { ask: string; flags: string[] }[];
  configuration?: {
    questions: ICondensedQuestion[];
  };
  name?: string;
  roundingType?: string;
}

interface IGetActiveTemplatesPayload extends IGetMasterTemplatesPayload {
  division: string;
  facilityId: string;
  roundingType: string;
  unitId: string;
}

interface IGetFullRoundSurveyPayload {
  division?: string;
  includeArchived: boolean;
  templateConfigId: string;
  unitId?: string;
}

/**
 * RTK Query API slice for template features.
 *
 * @remarks
 * Provides endpoints for:
 * - templateTree (`getTemplateTree`)
 * - Other config-related endpoints as defined
 *
 * @example
 * import { useGetTemplateTreeQuery } from 'src/redux/apis/Templates.api';
 * const { data } = useGetTemplateTreeQuery({ templateConfigId, userId });
 */
export const templatesApi = createApi({
  reducerPath: 'templatesApi',
  baseQuery: (operation: any) => operation,
  tagTypes: [
    'templates-master',
    'template-by-id',
    'questions',
    'template-tree',
    'round-survey',
    'template-doc',
    'templates-published'
  ],
  invalidationBehavior: 'immediately',
  endpoints: builder => ({
    // TODO: Typecast this way in the future
    getMasterTemplates: builder.query<
      ICondensedMasterTemplate[],
      IGetMasterTemplatesPayload
    >({
      keepUnusedDataFor: 30,
      query: payload =>
        getData(
          `${templateMasterUrl}?condensed=${payload.condensed}&archived=${payload.archived}`,
          {}
        ),
      transformResponse: response => {
        return response.sort(compareObjByStringKey('name'));
      },
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        { type: 'templates-master', id: JSON.stringify(payload) }
      ]
    }),
    postMasterTemplate: builder.mutation({
      query: ({
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        userId,
        ...rest
      }: IPostMasterTemplatePayload & { userId: string }) =>
        postData(templateMasterUrl, rest),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: (_result, _error, { userId, ...rest }) => [
        {
          type: 'template-tree',
          id: JSON.stringify({
            userId,
            templateConfigId: rest.templateConfigId
          })
        },
        { type: 'templates-master' }
      ]
    }),
    getTemplateById: builder.query({
      keepUnusedDataFor: 0,
      query: (templateId: string) =>
        getData(`${templateByIdUrl}?templateId=${templateId}`, {}),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, templateId) => [
        { type: 'template-by-id', id: templateId }
      ]
    }),
    getQuestions: builder.query<Question[], void>({
      query: () => getData(questionBankUrl, {}),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: () => [{ type: 'questions' }]
    }),
    postQuestion: builder.mutation<{ message: string }, Question>({
      query: (payload: Question) => postData(questionBankUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: () => [{ type: 'questions' }]
    }),
    updateQuestion: builder.mutation<{ message: string }, Question>({
      query: (payload: Question) => postData(questionBankUrl, payload),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: () => [{ type: 'questions' }]
    }),
    postTemplateConfig: builder.mutation({
      query: async ({
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        userId,
        ...rest
      }: IFullTemplate & { userId: string }) => {
        return postData(templateConfigUrl, rest);
      },
      transformResponse: processResponse,
      transformErrorResponse: processError,
      invalidatesTags: (_result, _error, { userId, ...rest }) => [
        {
          type: 'template-tree',
          id: JSON.stringify({
            userId,
            templateConfigId: rest.templateConfigId
          })
        }
      ]
    }),
    getTemplateTree: builder.query({
      query: (payload: { templateConfigId: string; userId: string }) =>
        getData(
          `${templateTreeUrl}?userId=${payload.userId}&templateConfigId=${payload.templateConfigId}`,
          {}
        ),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        { type: 'template-tree', id: JSON.stringify(payload) }
      ]
    }),
    getFullRoundSurvey: builder.query<
      IFullTemplate,
      IGetFullRoundSurveyPayload & Record<string, string>
    >({
      keepUnusedDataFor: 0,
      query: payload => {
        const params = new URLSearchParams(payload);
        return getData(`${fullSurveyUrl}?${params.toString()}`, {});
      },
      transformResponse: (response: IFullTemplate) => ({
        ...response,
        questions: response.questions ?? []
      }),
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        { type: 'round-survey', id: JSON.stringify(payload) }
      ]
    }),
    getTemplateDoc: builder.query<IFullTemplate, string>({
      keepUnusedDataFor: 0,
      query: docId => getData(`${templateDocUrl}?docId=${docId}`, {}),
      transformResponse: processResponse,
      transformErrorResponse: processError,
      providesTags: (_result, _error, docId) => [
        { type: 'template-doc', id: docId }
      ]
    }),
    getPublishedTemplatesByLocationAndType: builder.query<
      ICondensedMasterTemplate[],
      IGetActiveTemplatesPayload
    >({
      keepUnusedDataFor: 30,
      query: payload =>
        getData(
          `${templatePublishedByLocationAndTypeUrl}?roundingType=${payload.roundingType}&division=${payload.division}&facility=${payload.facilityId}&unit=${payload.unitId}&condensed=${payload.condensed}&archived=${payload.archived}`,
          {}
        ),
      transformResponse: response => {
        return response.sort(compareObjByStringKey('name'));
      },
      transformErrorResponse: processError,
      providesTags: (_result, _error, payload) => [
        { type: 'templates-published', id: JSON.stringify(payload) }
      ]
    })
  })
});

export const {
  useGetMasterTemplatesQuery,
  useLazyGetMasterTemplatesQuery,
  usePostMasterTemplateMutation,
  useGetTemplateByIdQuery,
  useLazyGetTemplateByIdQuery,
  useGetQuestionsQuery,
  useLazyGetQuestionsQuery,
  usePostQuestionMutation,
  useUpdateQuestionMutation,
  usePostTemplateConfigMutation,
  useGetTemplateTreeQuery,
  useLazyGetTemplateTreeQuery,
  useGetFullRoundSurveyQuery,
  useLazyGetFullRoundSurveyQuery,
  useGetTemplateDocQuery,
  useLazyGetTemplateDocQuery,
  useGetPublishedTemplatesByLocationAndTypeQuery,
  useLazyGetPublishedTemplatesByLocationAndTypeQuery
} = templatesApi;
